import type * as THREE from 'three';
import { compileThumb, renderPortrait, renderThumb } from '@somnhold/render';
import type { NpcId } from '@somnhold/content';

const cache = new Map<string, string>();
/** Each key's compile, once asked for (settled: its shaders are linked). */
const compiling = new Map<string, Promise<unknown>>();
const ready = new Set<string>();
/** Models drawn at least once (their shaders are linked). */
const drawn = new Set<string>();


/** Model thumbnail as a data URL (rendered once per key). */
export const thumb = (renderer: THREE.WebGLRenderer, key: string, size = 128, yaw?: number): string => {
  // Cached by size too: the same model is drawn small in a list and large on the sheet.
  const at = `${key}@${size}${yaw !== undefined ? `@${yaw}` : ''}`;
  let url = cache.get(at);
  if (!url) {
    url = renderThumb(renderer, key, size, 0, yaw !== undefined ? { yaw } : undefined);
    cache.set(at, url);
    drawn.add(key);
  }
  return url;
};

/** A cast member's head and shoulders as a data URL (rendered once). */
export const portraitThumb = (renderer: THREE.WebGLRenderer, id: NpcId, size = 160): string => {
  const key = `portrait:${id}:${size}`;
  let url = cache.get(key);
  if (!url) {
    url = renderPortrait(renderer, id, size);
    cache.set(key, url);
  }
  return url;
};

/**
 * The cards' shaders for these models, compiled ahead one after another (the next once the last is linked),
 * so a panel's first cards don't freeze the page. Stops when `alive` turns false.
 */
export const compileThumbs = async (renderer: THREE.WebGLRenderer, keys: string[], alive: () => boolean): Promise<void> => {
  for (const key of keys) {
    if (!alive()) return;
    if (drawn.has(key) || ready.has(key)) continue;
    let p = compiling.get(key);
    if (!p) {
      p = compileThumb(renderer, key).then(() => ready.add(key));
      compiling.set(key, p);
    }
    await p;
  }
};

/** Cards waiting to be drawn, one per animation frame. */
const queue: { renderer: THREE.WebGLRenderer; key: string; size: number; yaw?: number; done: (url: string) => void; alive: () => boolean }[] = [];
let pumping = false;
const pump = (): void => {
  const job = queue.shift();
  if (!job) {
    pumping = false;
    return;
  }
  if (job.alive()) job.done(thumb(job.renderer, job.key, job.size, job.yaw));
  requestAnimationFrame(pump);
};

/**
 * A card drawn soon rather than now: at once if it was drawn before at this size, else in its turn, one card per
 * frame and only once its shaders are compiled, so a sheet full of new cards never stalls a frame
 * (each card reads its pixels back: fourteen in one frame took 180 ms on a slowed phone).
 */
export const thumbLater = (renderer: THREE.WebGLRenderer, key: string, size: number, done: (url: string) => void, alive: () => boolean, yaw?: number): void => {
  const url = cache.get(`${key}@${size}${yaw !== undefined ? `@${yaw}` : ''}`);
  if (url) {
    done(url);
    return;
  }
  void compileThumbs(renderer, [key], alive).then(() => {
    queue.push({ renderer, key, size, ...(yaw !== undefined ? { yaw } : {}), done, alive });
    if (!pumping) {
      pumping = true;
      requestAnimationFrame(pump);
    }
  });
};
